/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { determineDataItemType } from 'dataFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPost, fetchPut } from 'FetchAPIs';
import { RESOURCE_TYPES } from 'dataConstants';

const dashboardBaseURL = '/api/dashboard';
const baseMasterURL = '/api/master';
const v2SummaryURL = '/api/hierarchies/deviceTypes';

const messages = defineMessages({
  errorLoadingSummary: 'Error loading summaries. {message}',
  errorAddingSummary: 'Error adding summary. {message}',
  errorRemovingSummary: 'Error removing summary. {message}',
  errorUpdatingSummary: 'Error updating summary. {message}',
  errorLoadingSummaryFilters: 'Error loading summary filters. {message}',
});

/**
 * The UI layer puts the current dashboard name into summaryDto: granularityName, but the new API expects this to be at the top level of the dashboard
 * @param {*} dashboard
 * @returns {object} updated dashboard payload
 */
const convertLegacyDashboardPayloadToNewPayload = dashboard => ({
  ...dashboard,
  ...(!isNil(dashboard?.summaryDto?.granularityName)
    ? { name: dashboard.summaryDto.granularityName }
    : {}), // the API payload changed to move our granularityName to the main dashboard name
  ...(!isEmpty(dashboard?.summaryDto) // only set a summaryDto if it actually exists
    ? { summaryDto: omit(dashboard.summaryDto, 'granularityName') }
    : {}),
});

/**
 * The UI layer returns the former granularityName into the name field of the main dashboard our UI expects these to be in granularityName of the summaryDto
 * @param {*} dashboard
 * @returns {object} updated dashboard to match our expectaiont
 */
const convertNewPayloadToLegacyDashboardPayload = dashboard => ({
  ...dashboard,
  ...(!isEmpty(dashboard?.summaryDto)
    ? {
        summaryDto: {
          ...dashboard.summaryDto,
          ...(!isNil(dashboard.name) ? { granularityName: dashboard.name } : {}),
        },
      }
    : {}),
});

export function addSummary(entityType, dashboard, userEmailId) {
  return fetchPost(
    `${dashboardBaseURL}/assetTypes/${entityType}`,
    {
      ...convertLegacyDashboardPayloadToNewPayload(dashboard),
      // TODO: move this to the caller
      dashboardMetadataJson: omit(dashboard.dashboardMetadataJson, [
        '_id',
        '_rev',
        'entitytype_id',
        'tenantId',
      ]),
    },
    { userEmailId }
  )
    .then(json => json)
    .catch(error => {
      throw new I18NError(messages.errorAddingSummary, {
        message: formatErrorResponse(error),
      });
    });
}

export function updateSummary(entityType, dashboard, userEmailId) {
  return fetchPut(
    `${dashboardBaseURL}/assetTypes/${entityType}/dashboards/${dashboard.dashboardMetaId}`,
    convertLegacyDashboardPayloadToNewPayload(dashboard),
    { userEmailId }
  )
    .then(json => json)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingSummary, {
        message: formatErrorResponse(error),
      });
    });
}

/** returns only summary dashboards for an entityType */
export const getSummaries = entityType =>
  fetchGet(`${dashboardBaseURL}/assetTypes/${entityType}`)
    .then(response =>
      Array.isArray(response)
        ? response
            .filter(dashboard => dashboard.type && dashboard.type === RESOURCE_TYPES.DEVICE_TYPE)
            // adding the entityType so I can make sure the current dashboards are valid for a given entity type
            .map(dashboard => ({
              ...convertNewPayloadToLegacyDashboardPayload(dashboard),
              entityType,
            }))
        : []
    )
    .catch(error => {
      throw new I18NError(messages.errorLoadingSummary, {
        message: formatErrorResponse(error),
      });
    });

/**
 *
 * @param {*} entityTypeName
 * @param {*} dashboardMetaId actually the dashboard ID
 * @returns
 */
export function getSummary(entityTypeName, dashboardMetaId) {
  return fetchGet(`${dashboardBaseURL}/assetTypes/${entityTypeName}/dashboards/${dashboardMetaId}`)
    .then(json => convertNewPayloadToLegacyDashboardPayload(json))
    .catch(error => {
      throw new I18NError(messages.errorLoadingSummary, {
        message: formatErrorResponse(error),
      });
    });
}

/**
 * Returns a list of valid dimension filter names and values for a Summary Dashboard that can be used to filter the data
 *
 * @param {*} entityTypeName
 * @param {'day'|'hour'} timeGrain granularity bucket size configured for the dashboard
 * @param {array} dimensions array of the dimensions that were configured for a summaryName
 * @returns {object} a list of the valid dimension values for a Summary Dashboard that can be used to filter.
 *                    the object is keyed by the dimension name, and the values are an array of all the unique values
 */
export function getSummaryFilters(entityTypeName, timeGrain, dimensions) {
  return fetchGet(`${baseMasterURL}/entity/type/${entityTypeName}/summaryFilters`, null, {
    timeGrain,
    dimensionNames: dimensions.join(','),
  }) // API layer gives it back in an array and our UI expects an object keyed by dimension name so we need to convert
    .then(response => {
      if (Array.isArray(response)) {
        return response.reduce((acc, newFilter) => {
          Object.keys(newFilter).forEach(filterField => {
            if (!acc[filterField]) {
              acc[filterField] = [newFilter[filterField]];
            } else {
              // add each value to the array
              acc[filterField] = uniq(acc[filterField].concat([newFilter[filterField]]));
            }
          });
          return acc;
        }, {});
      }
      return {};
    })
    .catch(error => {
      throw new I18NError(messages.errorLoadingSummaryFilters, {
        message: formatErrorResponse(error),
      });
    });
}

/**
 * returns all ‘Calculated’ Data items that are VALID for a particular Dashboard Grain and Dimensions.
 * (Valid Aggregated data items that match the dashboard constraints should come back, and all TRANSFORMED
 * data items should come back). Add the name of the Dashboard to each DataItem that so we could choose to
 * filter them out if we want from the UI. Don’t return any metrics that were created specifically for this
 * Dashboard. For example if you had built an Hourly Dashboard and it automatically created 3 new metrics,
 * and were now building a Daily Dashboard, the new 3 Hourly Dashboard metrics should probably not be listed
 * here, only Hourly metrics that the user manually created.
 * @param {string} entityType
 * @param {string} timeGrain the time grain of the previously configured dashboard
 * @param {Array<string>} dimensions dimension names used to locate the correct dashboard
 * @returns {Array<Object>} valid data items for summary dashboard
 */
export const getValidDataItemsForSummary = (entityType, timeGrain, dimensions) =>
  fetchGet(`${v2SummaryURL}/${entityType}/summary/dataItems`, null, {
    timeGrain,
    dimension: dimensions,
  })
    .then(data =>
      Array.isArray(data) // need to format the data item type to be correctly viewable
        ? data.map(dataItem => ({
            ...dataItem,
            type: determineDataItemType(dataItem),
          }))
        : data
    )
    .catch(error => {
      throw new I18NError(messages.errorData, error);
    });
